import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Gavel as BidIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  CheckCircle as AcceptedIcon,
  DoNotDisturb as RejectedIcon,
  HourglassEmpty as PendingIcon,
  AccessTime as ExpiredIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { BidWithRelations, BidStatus, Invoice } from '../../types/models';
import { formatCurrency, formatDate, formatDateOnly, getDaysRemaining, getDueStatusText } from '../../utils/formatters';

const BidsList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [bids, setBids] = useState<BidWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const fetchBids = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<BidWithRelations[]>('/bids');
      
      // Fetch invoice details for each bid - handle permissions carefully
      const bidsWithInvoices = await Promise.all(
        response.map(async (bid) => {
          if (!bid.invoice || !bid.invoice.id) {
            try {
              const invoiceData = await apiService.get<Invoice>(`/invoices/${bid.invoice_id}`);
              return { ...bid, invoice: invoiceData };
            } catch (invoiceErr: any) {
              console.error(`Error fetching invoice ${bid.invoice_id}:`, invoiceErr);
              // Create a placeholder invoice with minimal information
              // This prevents the UI from showing "Loading..." indefinitely
              const placeholderInvoice: Partial<Invoice> = {
                id: bid.invoice_id,
                invoice_number: `Invoice #${bid.invoice_id}`,
                amount: bid.amount, // Use bid amount as fallback
                currency: 'PYG', // Default currency
                due_date: bid.created_at, // Use bid date as fallback
                status: 'unknown', // Mark status as unknown
                has_permission_issue: true, // Flag to indicate permission issue
                
                // Required fields with placeholder values
                seller_id: 0,
                debtor_id: 0,
                issue_date: bid.created_at,
                tax_retention: 0,
                tax_amount: 0,
                net_amount: bid.amount,
                description: '',
                front_image_path: '',
                created_at: bid.created_at,
                updated_at: bid.created_at
              };
              
              return { 
                ...bid, 
                invoice: placeholderInvoice as Invoice
              };
            }
          }
          return bid;
        })
      );
      
      setBids(bidsWithInvoices);
    } catch (err: any) {
      console.error('Error fetching bids:', err);
      setError(err.response?.data?.detail || 'Error al cargar las ofertas');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchBids();
  }, []);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleViewInvoice = (invoiceId: number, hasPermissionIssue?: boolean) => {
    if (hasPermissionIssue) {
      setError('No tienes permisos para ver esta factura. Solo el vendedor de la factura o compradores pueden ver facturas verificadas y vendidas.');
      return;
    }
    navigate(`/marketplace/${invoiceId}`);
  };
  
  const getBidStatusChip = (status: BidStatus) => {
    switch (status) {
      case BidStatus.ACTIVE:
        return <Chip icon={<PendingIcon />} label="Activa" color="primary" size="small" />;
      case BidStatus.ACCEPTED:
        return <Chip icon={<AcceptedIcon />} label="Aceptada" color="success" size="small" />;
      case BidStatus.REJECTED:
        return <Chip icon={<RejectedIcon />} label="Rechazada" color="error" size="small" />;
      case BidStatus.EXPIRED:
        return <Chip icon={<ExpiredIcon />} label="Vencida" color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Panel de Control
        </Link>
        <Typography color="text.primary">Mis Ofertas</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom component="div">
          Mis Ofertas
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<BidIcon />}
          component={RouterLink}
          to="/marketplace"
        >
          Ir al Mercado
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : bids.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No has hecho ninguna oferta aún</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Visita el mercado para encontrar facturas y hacer ofertas
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<BidIcon />}
            component={RouterLink}
            to="/marketplace"
          >
            Ir al Mercado
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="my bids table">
              <TableHead>
                <TableRow>
                  <TableCell>Factura #</TableCell>
                  <TableCell>Deudor</TableCell>
                  <TableCell>Fecha de Vencimiento</TableCell>
                  <TableCell align="right">Monto de Factura</TableCell>
                  <TableCell align="right">Monto de Oferta</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha de Oferta</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bids
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((bid) => {
                    // If invoice is still missing after fetch attempt, show placeholder row
                    if (!bid.invoice) {
                      return (
                        <TableRow key={bid.id} hover>
                          <TableCell>{bid.invoice_id ? `#${bid.invoice_id}` : 'Desconocido'}</TableCell>
                          <TableCell>No disponible</TableCell>
                          <TableCell>No disponible</TableCell>
                          <TableCell align="right">No disponible</TableCell>
                          <TableCell align="right">{formatCurrency(bid.amount, 'PYG')}</TableCell>
                          <TableCell>{getBidStatusChip(bid.status)}</TableCell>
                          <TableCell>{formatDate(bid.created_at)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Ver Detalles de Oferta">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/bids/${bid.id}`)}
                                  color="primary"
                                >
                                  <BidIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Problema de permisos - No se pueden ver detalles de factura">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={true}
                                    color="default"
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    const daysRemaining = getDaysRemaining(bid.invoice.due_date);
                    const dueStatusText = getDueStatusText(daysRemaining);
                    
                    return (
                      <TableRow key={bid.id} hover>
                        <TableCell>{bid.invoice.invoice_number}</TableCell>
                        <TableCell>
                          {bid.invoice.debtor ? bid.invoice.debtor.name : 'Deudor Desconocido'}
                        </TableCell>
                        <TableCell>
                          <Box>
                            {formatDate(bid.invoice.due_date)}
                            <Typography variant="caption" display="block" color={dueStatusText.color}>
                              {dueStatusText.text}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(bid.invoice.amount, bid.invoice.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(bid.amount, bid.invoice.currency)}
                        </TableCell>
                        <TableCell>
                          {getBidStatusChip(bid.status)}
                        </TableCell>
                        <TableCell>
                          {formatDate(bid.created_at)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Ver Detalles de Oferta">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/bids/${bid.id}`)}
                                color="primary"
                              >
                                <BidIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {bid.invoice.has_permission_issue ? (
                              <Tooltip title="Problema de permisos - No se pueden ver detalles de factura">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={true}
                                    color="default"
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Ver Detalles de Factura">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewInvoice(bid.invoice_id)}
                                  color="primary"
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={bids.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Container>
  );
};

export default BidsList;
